import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMoneyBillWave } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';
import ChatbotAi from '../components/ChatbotAi';
import '../css/calculatorpages.css';

const EMICalculator = () => {
    const [loanAmount, setLoanAmount] = useState('');
    const [interestRate, setInterestRate] = useState('');
    const [loanTenure, setLoanTenure] = useState('');
    const [emi, setEmi] = useState(null);
    const [interestPayable, setInterestPayable] = useState(null);
    const [totalPayment, setTotalPayment] = useState(null);
    const [isIpadPro, setIsIpadPro] = useState(false);

    const navigate = useNavigate();

    const handleBackClick = () => {
        navigate('/#financial-calculators-section');
    };

    useEffect(() => {
        const checkIpadPro = () => {
            const width = window.innerWidth;
            setIsIpadPro(width === 1024 || width === 1366);
        };

        checkIpadPro();
        window.addEventListener('resize', checkIpadPro);

        return () => {
            window.removeEventListener('resize', checkIpadPro);
        };
    }, []);

    const calculateEMI = () => {
        const P = parseFloat(loanAmount);
        const R = parseFloat(interestRate) / 100 / 12;
        const N = parseFloat(loanTenure) * 12;

        if (P > 0 && R >= 0 && N > 0) {
            const calculatedEMI = (P * R * Math.pow(1 + R, N)) / (Math.pow(1 + R, N) - 1);
            const calculatedTotalPayment = calculatedEMI * N;
            const calculatedInterestPayable = calculatedTotalPayment - P;

            setEmi(calculatedEMI.toFixed(2));
            setInterestPayable(calculatedInterestPayable.toFixed(2));
            setTotalPayment(calculatedTotalPayment.toFixed(2));
        } else {
            setEmi(null);
            setInterestPayable(null);
            setTotalPayment(null);
        }
    };

    const ipadProLastInputStyle = isIpadPro ? {
        marginLeft: '0',
        marginRight: 'auto',
        alignSelf: 'flex-start'
    } : {};

    return (
        <div>
            <Helmet>
                <title>EMI Calculator | Calculate Your Loan EMI Easily</title>
                <meta name="description" content="Quickly calculate your loan EMI with our simple and accurate EMI calculator. Plan your loan payments clearly and stay in control of your finances." />
                <link rel="canonical" href="https://policysamjho.com/calculators/emi-calculator/" />
                <script type="application/ld+json">
                    {JSON.stringify({
                        "@context": "https://schema.org",
                        "@type": "SoftwareApplication",
                        "name": "EMI Calculator",
                        "description": "Calculate your loan EMI easily with our accurate and user-friendly EMI calculator. Plan your finances with confidence.",
                        "url": "https://policysamjho.com/calculators/emi-calculator/",
                        "applicationCategory": "FinancialApplication",
                        "operatingSystem": "Web",
                        "offers": {
                            "@type": "Offer",
                            "price": "0",
                            "priceCurrency": "INR"
                        },
                        "provider": {
                            "@type": "Organization",
                            "name": "Policy Samjho",
                            "url": "https://policysamjho.com/"
                        }
                    })}
                </script>
            </Helmet>

            <div className="calculator-page-container">
                <button onClick={handleBackClick} className="calculator-back-button">
                    Back
                </button>
                <div className="calculator-content-wrapper">
                    <div className="calculator-info-section">
                        <div className="calculator-header-content">
                            <FontAwesomeIcon icon={faMoneyBillWave} className="calculator-header-icon" />
                            <h1 className="calculator-title">EMI Calculator</h1>
                        </div>
                        <p className="calculator-description">
                            Taking a loan costs money in the form of processing fees, CIBIL check and most significantly loan interest. All of these charges along with the loan principal have to be paid back via equated monthly instalments or EMI. An EMI calculator is a tool that helps you figure out how much your monthly EMI payments will be so that you can pay back your loan in full.
                            Read on to know details of how an EMI calculator works, its calculation formula and how to use it so that you can figure out how much you have to pay monthly to pay out a loan that you plan to take.
                        </p>
                    </div>
                    <div className={`calculator-main-section ${emi === null ? 'no-result' : ''}`}>
                        <div className="calculator-input-section">
                            <div className="input-row-group">
                                <div className="input-group">
                                    <label htmlFor="loanAmount">Loan Amount (₹)</label>
                                    <input
                                        type="number"
                                        id="loanAmount"
                                        value={loanAmount}
                                        onChange={(e) => setLoanAmount(e.target.value)}
                                        placeholder="e.g. 500000"
                                    />
                                </div>
                                <div className="input-group">
                                    <label htmlFor="interestRate">Annual Interest Rate (%)</label>
                                    <input
                                        type="number"
                                        id="interestRate"
                                        value={interestRate}
                                        onChange={(e) => setInterestRate(e.target.value)}
                                        placeholder="e.g. 10"
                                    />
                                </div>
                            </div>
                            <div className="input-group" style={ipadProLastInputStyle}>
                                <label htmlFor="loanTenure">Loan Tenure (Years)</label>
                                <input
                                    type="number"
                                    id="loanTenure"
                                    value={loanTenure}
                                    onChange={(e) => setLoanTenure(e.target.value)}
                                    placeholder="e.g. 5"
                                />
                            </div>
                            <button onClick={calculateEMI} className="calculate-button">Calculate</button>
                        </div>
                        {emi !== null && (
                            <div className="calculator-result-section">
                                <h2>Your EMI Details</h2>
                                <div className="result-item">
                                    <span>Loan EMI (Monthly)</span>
                                    <span className="result-value">₹{emi}</span>
                                </div>
                                <div className="result-item">
                                    <span>Total Interest Payable</span>
                                    <span className="result-value">₹{interestPayable}</span>
                                </div>
                                <div className="result-item">
                                    <span>Total Payment (Principal + Interest)</span>
                                    <span className="result-value">₹{totalPayment}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </div>
            <ChatbotAi />
        </div>
    );
};

export default EMICalculator;